import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

interface FooterProps {
  showNewsletter: boolean;
  onClose: () => void;
}

export default function Footer({ showNewsletter, onClose }: FooterProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d4f2qmmiti2a337fh3o0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setTimeout(() => {
          setSubmitStatus('idle');
          onClose();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Newsletter Modal */}
      {showNewsletter && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-6">
          <div className="bg-zinc-900 border-2 border-red-600 max-w-md w-full p-8 relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl w-8 h-8 flex items-center justify-center"></i>
            </button>

            <h3 className="text-3xl font-black text-white mb-4">STAY UPDATED</h3>
            <p className="text-gray-400 mb-6">
              Subscribe to get the latest news, releases, and exclusive content from E.T.C.
            </p>

            <form id="newsletter-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="w-full px-4 py-3 bg-black border-2 border-zinc-700 focus:border-red-600 text-white text-sm outline-none transition-colors"
              />

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white font-bold tracking-wider transition-colors whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'SUBMITTING...' : 'SUBSCRIBE'}
              </button>

              {submitStatus === 'success' && (
                <p className="text-green-500 text-center text-sm">Successfully subscribed!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-500 text-center text-sm">Something went wrong. Please try again.</p>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-black border-t-2 border-zinc-900 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            {/* Brand */}
            <div>
              <img 
                src="https://static.readdy.ai/image/f2e9d0ecb0c8da9b7a5f1d855b73822d/053272dcaa033b9b5356f9612e504e77.png"
                alt="E.T.C Logo"
                className="h-16 w-auto mb-4"
              />
              <p className="text-gray-400 text-sm leading-relaxed">
                Chicago-raised. Vision-driven. Genre-fluid. Timeless.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-bold text-white mb-4 uppercase tracking-wider">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <a href="#music" className="text-gray-400 hover:text-red-500 transition-colors text-sm cursor-pointer">
                    Music
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-red-500 transition-colors text-sm cursor-pointer">
                    About
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-red-500 transition-colors text-sm cursor-pointer">
                    Gallery
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-red-500 transition-colors text-sm cursor-pointer">
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-lg font-bold text-white mb-4 uppercase tracking-wider">Connect</h4>
              <p className="text-gray-400 text-sm mb-4">
                For bookings and inquiries:
              </p>
              <a href="mailto:etc333music@gmail.com" className="text-red-500 hover:text-red-400 transition-colors text-sm cursor-pointer">
                etc333music@gmail.com
              </a>
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © 2025 E.T.C. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy-policy" className="text-gray-500 hover:text-red-500 transition-colors text-sm cursor-pointer">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="text-gray-500 hover:text-red-500 transition-colors text-sm cursor-pointer">
                Terms of Service
              </Link>
              <a 
                href="https://ericmoore.online" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-red-500 transition-colors text-sm cursor-pointer"
              >
                Powered by EMO
              </a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}